import React, { useEffect, useMemo, useRef, useState } from "react";
import { Cookie, ShieldCheck, Settings2, X } from "lucide-react";
import { Link } from "react-router-dom";
import { COOKIE_CATEGORIES, useCookieConsent } from "../context/CookieConsentContext";

/**
 * DSGVO-Cookie-Banner mit zwei Ansichten:
 *  1. Compact — kurze Info + gleichwertige Buttons (Akzeptieren / Ablehnen / Anpassen)
 *  2. Detail  — Kategorien mit Toggles + Beschreibungen + „Auswahl speichern"
 *
 * Design-Prinzipien:
 *  - Buttons in gleicher Größe/Farbe (kein „grün vs. grau"), erfüllt DSGVO
 *  - Optionale Kategorien standardmäßig deaktiviert
 *  - Focus-Management + Tastaturbedienung
 *  - respectiert prefers-reduced-motion
 */

function Toggle({ checked, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#2185D0] focus:ring-offset-2 " +
        (disabled
          ? "bg-white/20 cursor-not-allowed"
          : checked
          ? "bg-[#2185D0]"
          : "bg-white/25 hover:bg-white/35")
      }
    >
      <span
        className={
          "inline-block h-5 w-5 transform rounded-full bg-white transition-transform " +
          (checked ? "translate-x-5" : "translate-x-0.5")
        }
      />
    </button>
  );
}

export default function CookieBanner() {
  const {
    consent,
    isBannerOpen,
    openInDetailMode,
    setOpenInDetailMode,
    acceptAll,
    rejectAll,
    saveSelection,
  } = useCookieConsent();

  const initialSelection = useMemo(() => {
    const base = COOKIE_CATEGORIES.reduce((acc, c) => ({ ...acc, [c.id]: c.required }), {});
    return { ...base, ...(consent?.categories || {}) };
  }, [consent]);

  const [detailMode, setDetailMode] = useState(false);
  const [selection, setSelection] = useState(initialSelection);
  const rootRef = useRef(null);
  const firstFocusRef = useRef(null);

  // Sync when banner re-opens (e.g., via Footer-Link)
  useEffect(() => {
    if (isBannerOpen) {
      setSelection(initialSelection);
      setDetailMode(!!openInDetailMode);
    }
  }, [isBannerOpen, openInDetailMode, initialSelection]);

  // Focus first actionable element when opened
  useEffect(() => {
    if (isBannerOpen && firstFocusRef.current) {
      const t = setTimeout(() => firstFocusRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [isBannerOpen, detailMode]);

  // Simple focus-trap inside banner via TAB key
  useEffect(() => {
    if (!isBannerOpen) return;
    const handler = (e) => {
      if (e.key !== "Tab" || !rootRef.current) return;
      const focusables = rootRef.current.querySelectorAll(
        'button, [href], input, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isBannerOpen]);

  if (!isBannerOpen) return null;

  const openDetail = () => {
    setDetailMode(true);
    setOpenInDetailMode(true);
  };

  return (
    <>
      {/* Overlay (nur bei Detail-Modus, damit Compact den Nutzer nicht komplett blockiert) */}
      {detailMode && (
        <div
          aria-hidden="true"
          data-testid="cookie-overlay"
          className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm animate-hh-fade-in motion-reduce:animate-none"
        />
      )}

      <div
        ref={rootRef}
        role="dialog"
        aria-modal={detailMode ? "true" : "false"}
        aria-labelledby="hh-cookie-title"
        aria-describedby="hh-cookie-desc"
        data-testid="cookie-banner"
        className={
          "fixed z-[100] left-0 right-0 mx-auto " +
          (detailMode
            ? "top-0 bottom-0 max-w-2xl w-[calc(100vw-1rem)] sm:w-[calc(100vw-3rem)] flex items-center justify-center pointer-events-none"
            : "bottom-4 sm:bottom-6 max-w-4xl w-[calc(100vw-1rem)] sm:w-[calc(100vw-3rem)]")
        }
      >
        <div
          className={
            "bg-[#2E2E2E] text-white rounded-2xl border border-white/10 shadow-2xl overflow-hidden " +
            "animate-hh-slide-up motion-reduce:animate-none " +
            (detailMode ? "pointer-events-auto w-full max-h-[85vh] flex flex-col" : "w-full")
          }
        >
        {/* Header */}
        <div className="flex items-start gap-3 px-6 pt-6 sm:px-8 sm:pt-7">
          <div className="w-10 h-10 rounded-full bg-[#FFF44F]/15 flex items-center justify-center shrink-0">
            <Cookie className="w-5 h-5 text-[#FFF44F]" aria-hidden="true" />
          </div>
          <div className="flex-1">
            <h2
              id="hh-cookie-title"
              className="font-display text-lg sm:text-xl leading-tight"
            >
              {detailMode ? "Cookie-Einstellungen" : "Deine Cookie-Einstellungen"}
            </h2>
            <p
              id="hh-cookie-desc"
              className="mt-2 font-body text-sm text-white/75 leading-relaxed"
            >
              Wir nutzen ausschließlich technisch notwendige Cookies, damit die Website
              funktioniert. Optionale Cookies helfen uns, unsere Inhalte zu verbessern –
              nur wenn du zustimmst. Details findest du in der{" "}
              <Link
                to="/datenschutz"
                className="underline underline-offset-2 hover:text-[#FFF44F]"
                data-testid="cookie-banner-link-datenschutz"
              >
                Datenschutzerklärung
              </Link>
              .
            </p>
          </div>
          {detailMode && (
            <button
              type="button"
              onClick={() => setDetailMode(false)}
              aria-label="Detail-Ansicht schließen"
              data-testid="cookie-close-detail"
              className="ml-2 -mt-1 -mr-2 p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-[#2185D0]"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Detail: Kategorien */}
        {detailMode && (
          <div className="px-6 sm:px-8 mt-5 space-y-4 max-h-[50vh] overflow-y-auto">
            {COOKIE_CATEGORIES.map((cat) => (
              <div
                key={cat.id}
                className="rounded-xl border border-white/10 bg-white/5 p-4"
                data-testid={`cookie-cat-${cat.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-display text-sm sm:text-base">{cat.label}</span>
                      {cat.required && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-white/10 text-white/70">
                          Erforderlich
                        </span>
                      )}
                    </div>
                    <p className="mt-1.5 font-body text-xs sm:text-sm text-white/65 leading-relaxed">
                      {cat.description}
                    </p>
                  </div>
                  <Toggle
                    checked={selection[cat.id]}
                    disabled={cat.required}
                    onChange={(val) =>
                      setSelection((prev) => ({ ...prev, [cat.id]: val }))
                    }
                    label={`${cat.label} ${selection[cat.id] ? "deaktivieren" : "aktivieren"}`}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="px-6 sm:px-8 py-5 sm:py-6 mt-4 border-t border-white/10 bg-white/[0.02]">
          {detailMode ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                ref={firstFocusRef}
                type="button"
                onClick={() => saveSelection(selection)}
                data-testid="cookie-btn-save"
                className="hh-btn-cookie flex-1"
              >
                <ShieldCheck className="w-4 h-4" aria-hidden="true" />
                Auswahl speichern
              </button>
              <button
                type="button"
                onClick={rejectAll}
                data-testid="cookie-btn-reject"
                className="hh-btn-cookie flex-1"
              >
                Nur notwendige
              </button>
              <button
                type="button"
                onClick={acceptAll}
                data-testid="cookie-btn-accept-all-detail"
                className="hh-btn-cookie flex-1"
              >
                Alle akzeptieren
              </button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={rejectAll}
                data-testid="cookie-btn-reject-compact"
                className="hh-btn-cookie flex-1 order-2 sm:order-1"
              >
                Alle ablehnen
              </button>
              <button
                type="button"
                onClick={openDetail}
                data-testid="cookie-btn-customize"
                className="hh-btn-cookie flex-1 order-3 sm:order-2"
              >
                <Settings2 className="w-4 h-4" aria-hidden="true" />
                Anpassen
              </button>
              <button
                ref={firstFocusRef}
                type="button"
                onClick={acceptAll}
                data-testid="cookie-btn-accept-all"
                className="hh-btn-cookie flex-1 order-1 sm:order-3"
              >
                Alle akzeptieren
              </button>
            </div>
          )}
        </div>
        </div>
      </div>
    </>
  );
}
